(() => {
  const registry = new WeakMap();

  class MiniGearProductPage {
    constructor(section) {
      this.section = section;
      this.bound = [];
      this.drawerObserver = null;
      this.purchaseObserver = null;
      this.drawerScrollY = 0;
      this.drawerTouchStartX = 0;
      this.drawerTouchStartY = 0;

      this.drawerHistoryPushed = false;
      this.drawerClosingFromHistory = false;
      this.drawerHistoryFallbackTimer = null;

      this.init();
    }

    on(target, event, handler, options) {
      if (!target) return;

      target.addEventListener(event, handler, options);
      this.bound.push([target, event, handler, options]);
    }

    init() {
      this.ensureBuyNowButton();
      this.bindPurchaseAvailability();

      /*
        Do NOT hijack the normal Add to cart submit.
        Dawn's product-form.js must handle it so Theme settings > Cart > Drawer works correctly.
      */
      this.bindCartDrawerPageLock();
    }

    getProductForm() {
      return this.section.querySelector(
        '.product-form form[action*="/cart/add"], form[action*="/cart/add"][id^="product-form-"]'
      );
    }

    ensureBuyNowButton() {
      const buttonsWrap = this.section.querySelector(
        '.product-form__buttons'
      );
      const form = this.getProductForm();

      if (
        !buttonsWrap ||
        !form ||
        buttonsWrap.querySelector('[data-mg-buy-now]')
      ) {
        return;
      }

      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'mg-buy-now-button';
      button.dataset.mgBuyNow = 'true';
      button.textContent = this.section.dataset.mgBuyNowLabel || '';

      this.on(button, 'click', async () => {
        const formData = new FormData(form);

        button.disabled = true;
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(
            this.route('cart/add.js'),
            {
              method: 'POST',
              body: formData,
              headers: {
                Accept: 'application/json',
              },
            }
          );

          if (!response.ok) {
            throw new Error('Add to cart failed');
          }

          window.location.href = this.route('checkout');
        } catch (error) {
          button.disabled = false;
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');

          this.showMessage(this.section.dataset.mgTryAgainLabel || '');
        }
      });

      buttonsWrap.appendChild(button);
    }


    bindPurchaseAvailability() {
      const buttonsWrap = this.section.querySelector('.product-form__buttons');
      const submitButton = buttonsWrap?.querySelector('.product-form__submit');

      if (!buttonsWrap || !submitButton) return;

      const sync = () => {
        const labelNode = submitButton.querySelector('span');
        let text = labelNode?.textContent?.trim() || '';
        const addToCart = window.variantStrings?.addToCart || '';
        const isAvailableLabel = addToCart ? text === addToCart : !submitButton.disabled;
        const unavailable = submitButton.disabled && !isAvailableLabel;
        buttonsWrap.classList.toggle('is-mg-unavailable', unavailable);
      };

      sync();

      this.purchaseObserver = new MutationObserver(sync);
      this.purchaseObserver.observe(submitButton, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    bindCartDrawerPageLock() {
      const drawer = this.getCartDrawer();

      if (drawer) {
        this.drawerObserver = new MutationObserver(() =>
          this.syncCartDrawerState()
        );

        this.drawerObserver.observe(drawer, {
          attributes: true,
          attributeFilter: [
            'class',
            'open',
            'aria-hidden',
          ],
        });
      }

      this.on(
        document,
        'touchstart',
        (event) => {
          if (!this.isCartDrawerOpen()) return;
          if (!event.touches || !event.touches.length) return;

          this.drawerTouchStartX =
            event.touches[0].clientX;
          this.drawerTouchStartY =
            event.touches[0].clientY;
        },
        { passive: true }
      );

      this.on(
        document,
        'touchmove',
        (event) => {
          if (!this.isCartDrawerOpen()) return;
          if (!event.touches || !event.touches.length) return;

          const touch = event.touches[0];
          const dx =
            touch.clientX - this.drawerTouchStartX;
          const dy =
            touch.clientY - this.drawerTouchStartY;

          const drawerInner = event.target.closest(
            'cart-drawer .drawer__inner, cart-drawer .cart-drawer, cart-drawer'
          );

          /*
            Any touch outside the open drawer must not scroll
            the product page underneath.
          */
          if (!drawerInner) {
            if (event.cancelable) {
              event.preventDefault();
            }

            return;
          }

          /*
            Horizontal gestures inside the drawer should not leak
            to the product gallery.
          */
          if (
            Math.abs(dx) > Math.abs(dy) &&
            Math.abs(dx) > 8
          ) {
            if (event.cancelable) {
              event.preventDefault();
            }

            return;
          }

          /*
            Prevent scroll chaining from the drawer
            into the product page.
          */
          const scrollable = event.target.closest(
            'cart-drawer .drawer__inner, cart-drawer .cart-drawer__items, cart-drawer cart-drawer-items'
          );

          if (!scrollable) return;

          const canScroll =
            scrollable.scrollHeight >
            scrollable.clientHeight;

          if (!canScroll) {
            if (event.cancelable) {
              event.preventDefault();
            }

            return;
          }

          const atTop = scrollable.scrollTop <= 0;
          const atBottom =
            scrollable.scrollTop +
              scrollable.clientHeight >=
            scrollable.scrollHeight - 1;

          if (
            (atTop && dy > 0) ||
            (atBottom && dy < 0)
          ) {
            if (event.cancelable) {
              event.preventDefault();
            }
          }
        },
        { passive: false }
      );

      this.on(window, 'popstate', () => {
        /*
          Mobile browser back-swipe while cart drawer is open:
          consume the artificial drawer history state and close
          the drawer instead of leaving the product page.
        */
        if (
          this.isCartDrawerOpen() &&
          this.drawerHistoryPushed
        ) {
          this.drawerClosingFromHistory = true;
          this.closeCartDrawer();
          this.drawerHistoryPushed = false;

          window.clearTimeout(
            this.drawerHistoryFallbackTimer
          );

          this.drawerHistoryFallbackTimer =
            window.setTimeout(() => {
              this.drawerClosingFromHistory = false;
            }, 250);
        } else if (this.drawerClosingFromHistory) {
          this.drawerClosingFromHistory = false;
          this.drawerHistoryPushed = false;
        }
      });

      this.on(document, 'keydown', (event) => {
        if (
          event.key === 'Escape' &&
          this.isCartDrawerOpen()
        ) {
          window.setTimeout(
            () => this.syncCartDrawerState(),
            80
          );
        }
      });

      this.syncCartDrawerState();
    }

    getCartDrawer() {
      return document.querySelector('cart-drawer');
    }

    isMobileDrawerContext() {
      return (
        window.matchMedia &&
        window
          .matchMedia(
            '(max-width: 749px), (orientation: landscape) and (max-width: 989px) and (max-height: 520px)'
          )
          .matches
      );
    }

    isCartDrawerOpen() {
      const drawer = this.getCartDrawer();

      if (!drawer) return false;

      return (
        drawer.classList.contains('active') ||
        drawer.classList.contains('is-active') ||
        drawer.getAttribute('open') === 'true' ||
        drawer.getAttribute('aria-hidden') === 'false'
      );
    }

    syncCartDrawerState() {
      if (this.isCartDrawerOpen()) {
        this.lockProductPageBehindDrawer();
        this.pushDrawerHistoryState();
      } else {
        this.unlockProductPageBehindDrawer();
        this.consumeDrawerHistoryStateIfNeeded();
      }
    }

    pushDrawerHistoryState() {
      if (!this.isMobileDrawerContext()) return;
      if (this.drawerHistoryPushed) return;
      if (
        !window.history ||
        !window.history.pushState
      ) {
        return;
      }

      try {
        window.history.pushState(
          {
            mgCartDrawerOpen: true,
          },
          '',
          window.location.href
        );

        this.drawerHistoryPushed = true;
      } catch (error) {
        this.drawerHistoryPushed = false;
      }
    }

    consumeDrawerHistoryStateIfNeeded() {
      /*
        If the drawer was closed by tapping X / overlay,
        remove the artificial history entry.
      */
      if (!this.drawerHistoryPushed) return;
      if (this.drawerClosingFromHistory) return;
      if (!this.isMobileDrawerContext()) return;
      if (
        !window.history ||
        !window.history.back
      ) {
        return;
      }

      this.drawerClosingFromHistory = true;

      try {
        window.history.back();

        window.clearTimeout(
          this.drawerHistoryFallbackTimer
        );

        this.drawerHistoryFallbackTimer =
          window.setTimeout(() => {
            this.drawerClosingFromHistory = false;
            this.drawerHistoryPushed = false;
          }, 350);
      } catch (error) {
        this.drawerClosingFromHistory = false;
        this.drawerHistoryPushed = false;
      }
    }

    closeCartDrawer() {
      const drawer = this.getCartDrawer();

      if (!drawer) return;

      if (typeof drawer.close === 'function') {
        drawer.close();
      } else {
        const closeButton = drawer.querySelector(
          '.drawer__close'
        );

        if (closeButton) {
          closeButton.click();
        } else {
          drawer.classList.remove(
            'active',
            'is-active'
          );
          drawer.setAttribute(
            'aria-hidden',
            'true'
          );
          drawer.removeAttribute('open');
        }
      }

      window.setTimeout(
        () => this.syncCartDrawerState(),
        80
      );
    }

    lockProductPageBehindDrawer() {
      if (
        document.documentElement.classList.contains(
          'mg-cart-drawer-open'
        )
      ) {
        return;
      }

      this.drawerScrollY =
        window.scrollY ||
        document.documentElement.scrollTop ||
        0;

      document.documentElement.classList.add(
        'mg-cart-drawer-open'
      );
      document.body.classList.add(
        'mg-cart-drawer-open'
      );

      document.body.style.position = 'fixed';
      document.body.style.top =
        `-${this.drawerScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    unlockProductPageBehindDrawer() {
      if (
        !document.documentElement.classList.contains(
          'mg-cart-drawer-open'
        )
      ) {
        return;
      }

      document.documentElement.classList.remove(
        'mg-cart-drawer-open'
      );
      document.body.classList.remove(
        'mg-cart-drawer-open'
      );

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';

      window.scrollTo(
        0,
        this.drawerScrollY || 0
      );
    }

    showMessage(text) {
      const buttonsWrap = this.section.querySelector(
        '.product-form__buttons'
      );

      if (!buttonsWrap) return;

      let message = this.section.querySelector(
        '[data-mg-product-message]'
      );

      if (!message) {
        message = document.createElement('div');
        message.className =
          'mg-product-form-message';
        message.dataset.mgProductMessage = 'true';
        message.setAttribute('role', 'status');

        buttonsWrap.insertAdjacentElement(
          'afterend',
          message
        );
      }

      message.textContent = text;
      message.classList.add('is-visible');

      window.clearTimeout(this.messageTimer);

      this.messageTimer = window.setTimeout(() => {
        message.classList.remove('is-visible');
      }, 1800);
    }

    route(path) {
      const root =
        (window.Shopify &&
          window.Shopify.routes &&
          window.Shopify.routes.root) ||
        '/';

      return `${root}${path}`
        .replace(/\/+/g, '/')
        .replace(':/', '://');
    }

    destroy() {
      this.bound.forEach(
        ([target, event, handler, options]) => {
          target.removeEventListener(
            event,
            handler,
            options
          );
        }
      );

      this.bound = [];

      if (this.drawerObserver) {
        this.drawerObserver.disconnect();
        this.drawerObserver = null;
      }

      if (this.purchaseObserver) {
        this.purchaseObserver.disconnect();
        this.purchaseObserver = null;
      }

      window.clearTimeout(this.messageTimer);
      window.clearTimeout(
        this.drawerHistoryFallbackTimer
      );

      this.unlockProductPageBehindDrawer();

      this.drawerHistoryPushed = false;
      this.drawerClosingFromHistory = false;

      registry.delete(this.section);
    }
  }

  function init(container = document) {
    container
      .querySelectorAll(
        'product-info[id^="MainProduct-"]'
      )
      .forEach((section) => {
        if (registry.has(section)) return;

        registry.set(
          section,
          new MiniGearProductPage(section)
        );
      });
  }

  function destroy(container = document) {
    container
      .querySelectorAll(
        'product-info[id^="MainProduct-"]'
      )
      .forEach((section) => {
        const instance = registry.get(section);

        if (instance) {
          instance.destroy();
        }
      });
  }

  document.addEventListener(
    'DOMContentLoaded',
    () => init()
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => init(event.target)
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => destroy(event.target)
  );
})();